import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { updatePageMetadata, pageMetadata } from '../utils/pageMetadata';
import { featuredService, FeaturedPhoto } from '../services/featuredService';

const Featured: React.FC = () => {
  const [photos, setPhotos] = useState<FeaturedPhoto[]>([]);
  
  // Update page metadata on mount
  useEffect(() => {
    updatePageMetadata(pageMetadata.featured.title, pageMetadata.featured.description);
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [isCaptionExpanded, setIsCaptionExpanded] = useState(false);
  const [isDesktopCaptionExpanded, setIsDesktopCaptionExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);
  const captionTouchStartRef = useRef<number>(0);
  const captionTouchEndRef = useRef<number>(0);

  // Fetch featured photos on mount
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const fetchedPhotos = await featuredService.getFeaturedPhotos();
        setPhotos(fetchedPhotos);
        setError(null);
      } catch (err) {
        setError('Failed to load featured photos');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  // Navigation handlers
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
    setIsCaptionExpanded(false);
    setIsDesktopCaptionExpanded(false);
  }, [photos.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
    setIsCaptionExpanded(false);
    setIsDesktopCaptionExpanded(false);
  }, [photos.length]);

  const goToIndex = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsCaptionExpanded(false);
    setIsDesktopCaptionExpanded(false);
  }, []);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      // Swipe up - next photo
      goToNext();
    } else if (distance < -minSwipeDistance) {
      // Swipe down - previous photo
      goToPrevious();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  // Caption swipe handlers - collapse when scrolling up past the top
  const handleCaptionTouchStart = (e: React.TouchEvent) => {
    const touchY = e.targetTouches[0].clientY;
    captionTouchStartRef.current = touchY;
    captionTouchEndRef.current = touchY;
  };

  const handleCaptionTouchMove = (e: React.TouchEvent) => {
    if (!captionTouchStartRef.current) return;
    
    const currentY = e.targetTouches[0].clientY;
    captionTouchEndRef.current = currentY;
    
    const scrollTop = captionRef.current?.scrollTop || 0;
    const distance = currentY - captionTouchStartRef.current;
    
    // When at the top of scroll, prevent default for any vertical movement
    if (scrollTop === 0 && Math.abs(distance) > 5) {
      e.preventDefault();
    }
  };

  const handleCaptionTouchEnd = () => {
    if (!captionTouchStartRef.current || !captionTouchEndRef.current) {
      captionTouchStartRef.current = 0;
      captionTouchEndRef.current = 0;
      return;
    }
    
    const distance = captionTouchEndRef.current - captionTouchStartRef.current;
    const scrollTop = captionRef.current?.scrollTop || 0;
    
    // If at or near the top (including negative overscroll) and user swiped significantly
    if (scrollTop <= 10 && Math.abs(distance) > 50) {
      setIsCaptionExpanded(false);
    }

    captionTouchStartRef.current = 0;
    captionTouchEndRef.current = 0;
  };

  // Prevent pull-to-refresh on mobile view
  useEffect(() => {
    // Set CSS to prevent overscroll
    const originalBodyStyle = document.body.style.overscrollBehavior;
    const originalHtmlStyle = document.documentElement.style.overscrollBehavior;
    
    if (window.innerWidth < 768) { // md breakpoint
      document.body.style.overscrollBehavior = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
    }

    const preventPullToRefresh = (e: TouchEvent) => {
      // Prevent pull-to-refresh for the entire mobile featured view
      if (window.innerWidth < 768) { // md breakpoint
        const scrollY = window.scrollY || window.pageYOffset;
        if (scrollY === 0) {
          const touch = e.touches[0];
          // Detect if user is pulling down from the top
          if (touch && touch.clientY > 0) {
            e.preventDefault();
          }
        }
      }
    };

    // Add event listener with passive: false to allow preventDefault
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    
    return () => {
      document.removeEventListener('touchmove', preventPullToRefresh);
      // Restore original styles
      document.body.style.overscrollBehavior = originalBodyStyle;
      document.documentElement.style.overscrollBehavior = originalHtmlStyle;
    };
  }, []);

  // Additional prevention for caption area when expanded
  useEffect(() => {
    if (!isCaptionExpanded) return;

    const preventCaptionPullToRefresh = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const captionElement = captionRef.current;
      
      if (captionElement && captionElement.contains(target)) {
        const scrollTop = captionElement.scrollTop || 0;
        if (scrollTop === 0) {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('touchmove', preventCaptionPullToRefresh, { passive: false });
    
    return () => {
      document.removeEventListener('touchmove', preventCaptionPullToRefresh);
    };
  }, [isCaptionExpanded]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        goToPrevious();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading featured photos...</p>
        </div>
      </div>
    );
  }

  if (error || photos.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-100">
        <div className="text-center max-w-md p-4">
          <div className="text-6xl mb-4">📸</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {error ? 'Something went wrong' : 'No featured photos yet'}
          </h3>
          <p className="text-gray-600">
            {error || 'Check back soon for amazing photos from Sri Lanka!'}
          </p>
        </div>
      </div>
    );
  }

  const currentPhoto = photos[currentIndex];
  const cleanCredits = featuredService.cleanPhotoCredits(currentPhoto.photoCredits);

  return (
    <>
      {/* Mobile View - TikTok-style full screen swipable */}
      <div 
        className="md:hidden fixed inset-0 bg-black"
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: '56px', // Space for bottom navigation
          zIndex: 10,
          touchAction: 'pan-y',
          overscrollBehavior: 'contain'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative w-full h-full">
          {/* Background Image or Fallback */}
          {imageErrors.has(currentPhoto.id) ? (
            <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
              <div className="text-center p-8">
                <div className="text-6xl mb-4">🖼️</div>
                <h2 className="text-2xl font-bold text-white mb-2">{currentPhoto.title}</h2>
                <p className="text-gray-300 text-sm">Image loading blocked by CORS</p>
                <p className="text-gray-400 text-xs mt-2">Configure R2 bucket CORS settings</p>
              </div>
            </div>
          ) : (
            <div 
              className="w-full h-full"
              onClick={() => {
                if (isCaptionExpanded) {
                  setIsCaptionExpanded(false);
                }
              }}
            >
              <img
                src={currentPhoto.r2Urls.mobile}
                alt={currentPhoto.title}
                width="1080"
                height="1920"
                loading={currentIndex === 0 ? "eager" : "lazy"}
                fetchPriority={currentIndex === 0 ? "high" : "auto"}
                className="w-full h-full object-cover"
                style={{ aspectRatio: '9 / 16' }}
                onError={() => {
                  setImageErrors(prev => new Set(prev).add(currentPhoto.id));
                }}
              />
            </div>
          )}

          {/* Gradient overlay when caption is expanded */}
          {isCaptionExpanded && (
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)'
              }}
            />
          )}

          {/* Overlay Content */}
          <div className="absolute inset-0 flex flex-col justify-between p-4 pb-4 pointer-events-none">
            {/* Top: Title */}
            <div className="pointer-events-auto">
              <h1 
                className="text-lg font-bold text-white drop-shadow-lg"
                style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}
              >
                {currentPhoto.title}
              </h1>
            </div>

            {/* Bottom: Credits, Caption, and Google Button */}
            <div className="space-y-2 pointer-events-auto">
              {/* Photo Credits */}
              {cleanCredits && (
                <p 
                  className="text-sm text-white drop-shadow-lg"
                  style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}
                >
                  {cleanCredits}
                </p>
              )}

              {/* Caption - Instagram-style expand/collapse */}
              <div className="flex items-end justify-between gap-3" key={currentPhoto.id}>
                <div 
                  ref={captionRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isCaptionExpanded) {
                      setIsCaptionExpanded(true);
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    if (isCaptionExpanded) {
                      handleCaptionTouchStart(e);
                    }
                  }}
                  onTouchMove={(e) => {
                    e.stopPropagation();
                    if (isCaptionExpanded) {
                      handleCaptionTouchMove(e);
                    }
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    if (isCaptionExpanded) {
                      handleCaptionTouchEnd();
                    }
                  }}
                  className={`flex-1 rounded-lg cursor-pointer transition-all duration-300 ${
                    isCaptionExpanded 
                      ? 'bg-transparent max-h-screen overflow-y-auto pb-32' 
                      : 'bg-black bg-opacity-20 max-h-20 overflow-hidden p-3'
                  }`}
                  style={{ 
                    scrollbarWidth: 'none',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: isCaptionExpanded ? 'pan-y' : 'auto',
                    overscrollBehavior: 'contain'
                  }}
                >
                  {/* Swipe handle - only show when expanded */}
                  {isCaptionExpanded && (
                    <div className="sticky top-0 flex justify-center py-2 pb-3 bg-gradient-to-b from-black/40 to-transparent">
                      <div className="w-12 h-1 bg-white rounded-full opacity-60" />
                    </div>
                  )}
                  
                  <div className={`text-sm text-white leading-relaxed transition-all duration-300 ${
                    isCaptionExpanded ? 'px-3' : 'line-clamp-1'
                  }`}>
                    {isCaptionExpanded 
                      ? currentPhoto.caption.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className={idx > 0 ? 'mt-4' : ''}>
                            {paragraph}
                          </p>
                        ))
                      : `${currentPhoto.caption.split('\n')[0]}...`
                    }
                  </div>
                </div>

                {/* Google Maps Button */}
                {currentPhoto.locationLink && (
                  <a
                    href={currentPhoto.locationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 p-3 bg-blue-600 rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={20} className="text-white" />
                  </a>
                )}
              </div>

            </div>
          </div>

          {/* Vertical Progress Indicator - Left Side */}
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex flex-col gap-2 pointer-events-none">
            {photos.map((_, idx) => (
              <div
                key={idx}
                className={`w-1 rounded-full transition-all ${
                  idx === currentIndex 
                    ? 'h-8 bg-white' 
                    : 'h-1 bg-white bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Desktop View - Carousel with thumbnails */}
      <div className="hidden md:flex flex-col h-full bg-gray-100">
        {/* Main Image Area */}
        <div className="flex-1 flex items-center justify-center p-8 relative">
          {/* Previous Button */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            aria-label="Previous photo"
          >
            <ChevronLeft size={24} className="text-gray-800" />
          </button>

          {/* Image Container */}
          <div 
            ref={containerRef}
            className="relative max-w-6xl w-full h-full flex items-center justify-center"
          >
            <div className="relative w-full h-full flex flex-col items-center justify-center gap-4">
              {/* Image Container with Title Overlay */}
              <div className="relative w-full max-w-4xl">
                {/* Image or Fallback */}
                {imageErrors.has(currentPhoto.id) ? (
                  <div className="w-full h-96 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg shadow-2xl flex items-center justify-center">
                    <div className="text-center p-8">
                      <div className="text-6xl mb-4">🖼️</div>
                      <h2 className="text-3xl font-bold text-white mb-2">{currentPhoto.title}</h2>
                      <p className="text-gray-300 text-sm">Image loading blocked by CORS</p>
                      <p className="text-gray-400 text-xs mt-2">Configure R2 bucket CORS settings</p>
                    </div>
                  </div>
                ) : (
                  <img
                    src={currentPhoto.r2Urls.desktop}
                    alt={currentPhoto.title}
                    width="1920"
                    height="1080"
                    loading={currentIndex === 0 ? "eager" : "lazy"}
                    fetchPriority={currentIndex === 0 ? "high" : "auto"}
                    className="w-full max-h-[calc(100vh-400px)] object-contain rounded-lg shadow-2xl"
                    style={{ aspectRatio: '16 / 9' }}
                    onError={() => {
                      setImageErrors(prev => new Set(prev).add(currentPhoto.id));
                    }}
                  />
                )}

                {/* Title Overlay on Image */}
                <div className="absolute top-0 left-0 p-4">
                  <h1 
                    className="text-xl font-bold text-white drop-shadow-lg bg-black bg-opacity-40 px-4 py-2 rounded-lg backdrop-blur-sm"
                  >
                    {currentPhoto.title}
                  </h1>
                </div>
              </div>

              {/* Caption Box Below Image */}
              <div 
                className="w-full max-w-4xl bg-white/70 backdrop-blur-md rounded-2xl p-5 shadow-xl border border-white/20 transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
                }}
                key={currentPhoto.id}
              >
                {/* Photo Credits */}
                {cleanCredits && (
                  <p className="text-xs text-gray-700 mb-3 font-medium opacity-80">
                    {cleanCredits}
                  </p>
                )}

                <div className="flex items-start justify-between gap-4">
                  {/* Caption */}
                  <div className="flex-1">
                    <div className={`text-sm text-gray-900 leading-relaxed transition-all duration-300 ${
                      isDesktopCaptionExpanded ? '' : 'line-clamp-2'
                    }`}>
                      {currentPhoto.caption.split('\n\n').map((paragraph, idx) => (
                        <p key={idx} className={idx > 0 ? 'mt-3' : ''}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    {currentPhoto.caption.length > 150 && (
                      <button
                        onClick={() => setIsDesktopCaptionExpanded(!isDesktopCaptionExpanded)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-semibold mt-2 inline-flex items-center transition-colors duration-200 hover:underline"
                      >
                        {isDesktopCaptionExpanded ? 'View less' : 'View more'}
                      </button>
                    )}
                  </div>

                  {/* Google Maps Button */}
                  {currentPhoto.locationLink && (
                    <a
                      href={currentPhoto.locationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 p-3 bg-blue-600 rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                    >
                      <ExternalLink size={20} className="text-white" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={goToNext}
            className="absolute right-4 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            aria-label="Next photo"
          >
            <ChevronRight size={24} className="text-gray-800" />
          </button>
        </div>

        {/* Thumbnail Strip */}
        <div className="flex-shrink-0 bg-gradient-to-b from-gray-50 to-white border-t border-gray-200 p-4 py-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex gap-3 overflow-x-auto pb-2 px-2" style={{ scrollbarWidth: 'thin', overflowY: 'visible' }}>
              {photos.map((photo, idx) => (
                <button
                  key={photo.id}
                  onClick={() => goToIndex(idx)}
                  className={`flex-shrink-0 relative rounded-xl overflow-hidden transition-all duration-300 ${
                    idx === currentIndex 
                      ? 'scale-110 shadow-2xl' 
                      : 'opacity-60 hover:opacity-100 hover:scale-105 shadow-md'
                  }`}
                >
                  {imageErrors.has(photo.id) ? (
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                      <span className="text-white text-xs text-center px-1">
                        {photo.title.substring(0, 10)}
                      </span>
                    </div>
                  ) : (
                    <img
                      src={photo.r2Urls.thumbnail}
                      alt={photo.title}
                      width="64"
                      height="64"
                      loading="lazy"
                      className="w-16 h-16 object-cover"
                      onError={(e) => {
                        setImageErrors(prev => new Set(prev).add(photo.id));
                      }}
                    />
                  )}
                  {/* Modern selection indicator - bottom bar */}
                  {idx === currentIndex && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>
                  )}
                  {/* Subtle overlay for unselected */}
                  {idx !== currentIndex && (
                    <div className="absolute inset-0 bg-black bg-opacity-20 hover:bg-opacity-0 transition-opacity duration-300"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Featured;

